import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CameraAlt from '@mui/icons-material/CameraAlt';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import CommuteIcon from '@mui/icons-material/Commute';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DoorBackIcon from '@mui/icons-material/DoorBack';
import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import GroupIcon from '@mui/icons-material/Group';
import HomeIcon from '@mui/icons-material/Home';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import PersonIcon from '@mui/icons-material/Person';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { ListItem } from '@mui/material';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import MuiDrawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { FunctionComponent, PropsWithChildren } from 'react';

import { LogoutDialog } from '../../../auth/components/LogoutDialog/LogoutDialog';
import { useLayout } from './useLayout';

const drawerWidth = 240;

interface AppBarProps extends MuiAppBarProps {
  open: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  '& .MuiDrawer-paper': {
    position: 'relative',
    whiteSpace: 'nowrap',
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: 'border-box',
    ...(!open && {
      overflowX: 'hidden',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

export type LayoutTab =
  | 'dashboard'
  | 'userInfos'
  | 'userImages'
  | 'adminEvents'
  | 'adminBedrooms'
  | 'adminEventUsers'
  | 'adminEventTransactions'
  | 'adminPlaces'
  | 'adminDjRequests'
  | 'adminUsers'
  | 'eventRegister'
  | 'caution'
  | 'payment'
  | 'eventUsers'
  | 'bedroom'
  | 'carpool'
  | 'contact'
  | 'validation'
  | 'target'
  | 'tinder'
  | 'dj';

interface LayoutProps {
  title: string;
  activeTab: LayoutTab;
  headerType?: 'error' | 'default' | 'success';
  noPadding?: boolean;
}

export const Layout: FunctionComponent<PropsWithChildren<LayoutProps>> = ({
  title,
  activeTab,
  children,
  headerType = 'default',
  noPadding = false,
}) => {
  const {
    isDrawerOpen,
    toggleDrawer,
    isLogoutDialogOpen,
    onCancelLogout,
    onConfirmLogout,
    onLogout,
    isAdmin,
    isStaff,
    onDashboardPress,
    onEventsPress,
    onUserInfosPress,
    onUserImagesPress,
    onEventRegisterPress,
    onAdminEventUsersPress,
    onAdminEventTransactionsPress,
    onAdminUsersPress,
    onCautionPress,
    onPaymentPress,
    onEventUsersPress,
    onBedroomPress,
    onCarpoolPress,
    onContactPress,
    onAdminPlacesPress,
    onAdminBedroomsPress,
    hasPaidTicket,
    isValidator,
    onAdminValidationPress,
    isTentOnly,
    onTargetPress,
    onTinderPress,
    onDjPress,
    onAdminDjRequestsPress,
    isValidated,
    isTargetEnabled,
  } = useLayout();

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="absolute" open={isDrawerOpen}>
        <Toolbar
          sx={{
            pr: '24px',
            bgcolor:
              headerType === 'error'
                ? 'red'
                : headerType === 'success'
                  ? 'green'
                  : 'primary.main',
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: '36px',
              ...(isDrawerOpen && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {title}
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={isDrawerOpen}>
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            px: [1],
          }}
        >
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </Toolbar>
        <Divider />
        <List component="nav">
          <ListItemButton
            selected={activeTab === 'dashboard'}
            onClick={onDashboardPress}
          >
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>

          <ListItemButton
            selected={activeTab === 'userInfos'}
            onClick={onUserInfosPress}
          >
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary="Mes infos persos" />
          </ListItemButton>

          <ListItemButton
            selected={activeTab === 'userImages'}
            onClick={onUserImagesPress}
          >
            <ListItemIcon>
              <CameraAlt />
            </ListItemIcon>
            <ListItemText primary="Mes photos" />
          </ListItemButton>

          <ListItemButton
            selected={activeTab === 'eventRegister'}
            onClick={onEventRegisterPress}
          >
            <ListItemIcon>
              <NoteAltIcon />
            </ListItemIcon>
            <ListItemText primary="Inscription" />
          </ListItemButton>
          <ListItemButton
            selected={activeTab === 'payment'}
            onClick={onPaymentPress}
          >
            <ListItemIcon>
              <ShoppingCartIcon />
            </ListItemIcon>
            <ListItemText primary="Paiement" />
          </ListItemButton>
          <ListItemButton
            selected={activeTab === 'caution'}
            onClick={onCautionPress}
          >
            <ListItemIcon>
              <AccountBalanceIcon />
            </ListItemIcon>
            <ListItemText primary="Caution" />
          </ListItemButton>
          <ListItemButton
            selected={activeTab === 'eventUsers'}
            onClick={onEventUsersPress}
          >
            <ListItemIcon>
              <GroupIcon />
            </ListItemIcon>
            <ListItemText primary="Liste des inscrits" />
          </ListItemButton>
          {hasPaidTicket && !isTentOnly ? (
            <ListItemButton
              selected={activeTab === 'bedroom'}
              onClick={onBedroomPress}
            >
              <ListItemIcon>
                <HomeIcon />
              </ListItemIcon>
              <ListItemText primary="Ma Chambre" />
            </ListItemButton>
          ) : null}

          {hasPaidTicket || isStaff ? (
            <ListItemButton
              selected={activeTab === 'carpool'}
              onClick={onCarpoolPress}
            >
              <ListItemIcon>
                <CommuteIcon />
              </ListItemIcon>
              <ListItemText primary="Covoiturage" />
            </ListItemButton>
          ) : null}

          {hasPaidTicket ? (
            <ListItemButton selected={activeTab === 'dj'} onClick={onDjPress}>
              <ListItemIcon>
                <MusicNoteIcon />
              </ListItemIcon>
              <ListItemText primary="Fiche DJ" />
            </ListItemButton>
          ) : null}

          {isValidated ? (
            <>
              <Divider sx={{ my: 1 }} />
              <ListItem>
                <ListItemText secondary="Jeux" />
              </ListItem>

              {isTargetEnabled ? (
                <ListItemButton
                  selected={activeTab === 'target'}
                  onClick={onTargetPress}
                >
                  <ListItemIcon>
                    <CrisisAlertIcon />
                  </ListItemIcon>
                  <ListItemText primary="Target" />
                </ListItemButton>
              ) : null}

              <ListItemButton
                selected={activeTab === 'tinder'}
                onClick={onTinderPress}
              >
                <ListItemIcon>
                  <LocalFireDepartmentIcon />
                </ListItemIcon>
                <ListItemText primary="Meetic" />
              </ListItemButton>
            </>
          ) : null}

          {isAdmin || isStaff ? (
            <>
              <Divider sx={{ my: 1 }} />
              <ListItem>
                <ListItemText secondary="Administration" />
              </ListItem>
              {isAdmin ? (
                <ListItemButton
                  onClick={onEventsPress}
                  selected={activeTab === 'adminEvents'}
                >
                  <ListItemIcon>
                    <EventIcon />
                  </ListItemIcon>
                  <ListItemText primary="Tous les événements" />
                </ListItemButton>
              ) : null}
              {isAdmin ? (
                <ListItemButton
                  onClick={onAdminBedroomsPress}
                  selected={activeTab === 'adminBedrooms'}
                >
                  <ListItemIcon>
                    <HomeIcon />
                  </ListItemIcon>
                  <ListItemText primary="Chambres" />
                </ListItemButton>
              ) : null}
              <ListItemButton
                onClick={onAdminEventUsersPress}
                selected={activeTab === 'adminEventUsers'}
              >
                <ListItemIcon>
                  <PermContactCalendarIcon />
                </ListItemIcon>
                <ListItemText primary="Participants" />
              </ListItemButton>
              {isAdmin ? (
                <ListItemButton
                  onClick={onAdminDjRequestsPress}
                  selected={activeTab === 'adminDjRequests'}
                >
                  <ListItemIcon>
                    <MusicNoteIcon />
                  </ListItemIcon>
                  <ListItemText primary="Demandes DJ" />
                </ListItemButton>
              ) : null}
              {isAdmin ? (
                <ListItemButton
                  onClick={onAdminEventTransactionsPress}
                  selected={activeTab === 'adminEventTransactions'}
                >
                  <ListItemIcon>
                    <AttachMoneyIcon />
                  </ListItemIcon>
                  <ListItemText primary="Transactions" />
                </ListItemButton>
              ) : null}
              {isAdmin ? (
                <ListItemButton
                  onClick={onAdminPlacesPress}
                  selected={activeTab === 'adminPlaces'}
                >
                  <ListItemIcon>
                    <HomeIcon />
                  </ListItemIcon>
                  <ListItemText primary="Lieux" />
                </ListItemButton>
              ) : null}
              {isValidator ? (
                <ListItemButton
                  onClick={onAdminValidationPress}
                  selected={activeTab === 'validation'}
                >
                  <ListItemIcon>
                    <DoorBackIcon />
                  </ListItemIcon>
                  <ListItemText primary="Validation" />
                </ListItemButton>
              ) : null}
              {isAdmin ? (
                <ListItemButton
                  onClick={onAdminUsersPress}
                  selected={activeTab === 'adminUsers'}
                >
                  <ListItemIcon>
                    <GroupIcon />
                  </ListItemIcon>
                  <ListItemText primary="Utilisateurs" />
                </ListItemButton>
              ) : null}
            </>
          ) : null}
          <Divider sx={{ my: 1 }} />
          <ListItemButton
            onClick={onContactPress}
            selected={activeTab === 'contact'}
          >
            <ListItemIcon>
              <EmailIcon />
            </ListItemIcon>
            <ListItemText primary="Nous contacter" />
          </ListItemButton>
          <ListItemButton sx={{ color: 'error.main' }} onClick={onLogout}>
            <ListItemIcon sx={{ color: 'error.main' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Se déconnecter" />
          </ListItemButton>
        </List>
      </Drawer>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          flexGrow: 1,
          height: '100vh',
          overflow: 'auto',
        }}
      >
        <Toolbar />
        <Container
          maxWidth="lg"
          sx={{
            mt: noPadding ? 0 : 4,
            mb: noPadding ? 0 : 4,
            px: noPadding ? 0 : undefined,
          }}
        >
          {children}
        </Container>
      </Box>
      <LogoutDialog
        open={isLogoutDialogOpen}
        onCancel={onCancelLogout}
        onLogout={onConfirmLogout}
      />
    </Box>
  );
};
